import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, LogOut, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { MemberList } from "@/components/MemberList";
import { GameList } from "@/components/GameList";
import { GameWheel } from "@/components/GameWheel";
import { TournamentSection } from "@/components/TournamentSection";
import { SteamSignInButton } from "@/components/SteamSignInButton";
import { Skeleton } from "@/components/ui/skeleton";
import {
  api,
  ApiError,
  compareGamesByName,
  type CommonGame,
  type Me,
  type RoomDetails,
  type RoomMode,
} from "@/lib/api";
import { useRoomSocket } from "@/hooks/useSocket";

interface RoomProps {
  me: Me | null;
  onMeChange: (me: Me | null) => void;
}

export function Room({ me, onMeChange }: RoomProps) {
  const params = useParams<{ code: string }>();
  const code = (params.code ?? "").toUpperCase();
  const navigate = useNavigate();
  const [details, setDetails] = useState<RoomDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [highlightAppId, setHighlightAppId] = useState<number | undefined>();
  const [disabled, setDisabled] = useState<Set<number>>(new Set());
  const [mode, setMode] = useState<RoomMode>("normal");
  const [isSpinning, setIsSpinning] = useState(false);
  const [isTournamentSpinning, setIsTournamentSpinning] = useState(false);
  const [roomTab, setRoomTab] = useState<"wheel" | "tournament">("wheel");

  const socket = useRoomSocket(me ? code : undefined);

  const refresh = useCallback(async () => {
    if (!me || !code) return;
    try {
      const fresh = await api.getRoom(code);
      setDetails(fresh);
      setDisabled(new Set(fresh.disabledAppIds));
      setMode(fresh.mode);
      setNotFound(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setNotFound(true);
      } else {
        toast.error(err instanceof Error ? err.message : "Could not load room");
      }
    } finally {
      setLoading(false);
    }
  }, [me, code]);

  useEffect(() => {
    if (!me) {
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const fresh = await api.joinRoom(code);
        if (!cancelled) {
          setDetails(fresh);
          setDisabled(new Set(fresh.disabledAppIds));
          setMode(fresh.mode);
          setNotFound(false);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
        } else {
          toast.error(err instanceof Error ? err.message : "Could not join room");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [me, code]);

  useEffect(() => {
    if (!me) return;
    const onMember = () => refresh();
    const onLibrary = () => refresh();
    const onResult = (p: { appId: number }) => setHighlightAppId(p.appId);
    const onToggled = (p: { appId: number; disabled: boolean }) => {
      setDisabled((prev) => {
        const next = new Set(prev);
        if (p.disabled) next.add(p.appId);
        else next.delete(p.appId);
        return next;
      });
    };
    const onMode = (p: { mode: RoomMode }) => {
      setMode(p.mode);
    };
    const onCustomAdded = (p: {
      appId: number;
      name: string;
      addedBySteamId: string | null;
    }) => {
      setDetails((prev) => {
        if (!prev) return prev;
        if (prev.commonGames.some((g) => g.appId === p.appId)) return prev;
        const next: CommonGame = {
          appId: p.appId,
          name: p.name,
          iconUrl: null,
          custom: true,
          addedBySteamId: p.addedBySteamId,
        };
        return {
          ...prev,
          commonGames: [...prev.commonGames, next].sort(compareGamesByName),
        };
      });
    };
    const onCustomRemoved = (p: { appId: number }) => {
      setDetails((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          commonGames: prev.commonGames.filter((g) => g.appId !== p.appId),
        };
      });
      setDisabled((prev) => {
        if (!prev.has(p.appId)) return prev;
        const next = new Set(prev);
        next.delete(p.appId);
        return next;
      });
    };
    socket.on("member:joined", onMember);
    socket.on("member:left", onMember);
    socket.on("library:synced", onLibrary);
    socket.on("wheel:result", onResult);
    socket.on("wheel:toggled", onToggled);
    socket.on("room:mode", onMode);
    socket.on("custom:added", onCustomAdded);
    socket.on("custom:removed", onCustomRemoved);
    return () => {
      socket.off("member:joined", onMember);
      socket.off("member:left", onMember);
      socket.off("library:synced", onLibrary);
      socket.off("wheel:result", onResult);
      socket.off("wheel:toggled", onToggled);
      socket.off("room:mode", onMode);
      socket.off("custom:added", onCustomAdded);
      socket.off("custom:removed", onCustomRemoved);
    };
  }, [socket, me, refresh]);

  const toggleGame = useCallback(
    (appId: number, nextDisabled: boolean) => {
      if (isSpinning) return;
      setDisabled((prev) => {
        const next = new Set(prev);
        if (nextDisabled) next.add(appId);
        else next.delete(appId);
        return next;
      });
      socket.emit("wheel:toggle", {
        code,
        appId,
        disabled: nextDisabled,
      });
    },
    [socket, code, isSpinning],
  );

  const changeMode = useCallback(
    (next: RoomMode) => {
      if (isSpinning) return;
      setMode(next);
      socket.emit("room:setMode", { code, mode: next });
    },
    [socket, code, isSpinning],
  );

  const addCustomGame = useCallback(
    (name: string) => {
      if (isSpinning) return;
      const trimmed = name.trim();
      if (trimmed.length === 0) return;
      socket.emit("custom:add", { code, name: trimmed });
    },
    [socket, code, isSpinning],
  );

  const removeCustomGame = useCallback(
    (appId: number) => {
      if (isSpinning) return;
      socket.emit("custom:remove", { code, appId });
    },
    [socket, code, isSpinning],
  );

  const bulkToggleGames = useCallback(
    (nextDisabled: boolean) => {
      if (isSpinning) return;
      if (!details) return;
      const targets = nextDisabled
        ? details.commonGames.filter((g) => !disabled.has(g.appId))
        : details.commonGames.filter((g) => disabled.has(g.appId));
      if (targets.length === 0) return;
      setDisabled(() => {
        const next = new Set<number>();
        if (nextDisabled) {
          for (const g of details.commonGames) next.add(g.appId);
        }
        return next;
      });
      for (const g of targets) {
        socket.emit("wheel:toggle", {
          code,
          appId: g.appId,
          disabled: nextDisabled,
        });
      }
    },
    [socket, code, details, disabled, isSpinning],
  );

  async function copyLink() {
    const url = `${window.location.origin}/r/${code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Couldn't copy. Link: " + url);
    }
  }

  async function handleSync() {
    const t = toast.loading("Syncing your Steam library...");
    try {
      const result = await api.syncLibrary();
      toast.success(`Synced ${result.count} games`, { id: t });
      onMeChange(await api.me());
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sync failed", { id: t });
    }
  }

  async function handleLeave() {
    try {
      await api.leaveRoom(code);
      navigate("/");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Leave failed");
    }
  }

  if (!me) {
    return (
      <main className="container flex flex-1 items-center justify-center py-10">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Sign in to join room {code}</CardTitle>
            <CardDescription>
              Sign in with Steam to add your library to this room and spin the wheel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SteamSignInButton size="lg" />
          </CardContent>
        </Card>
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="container flex flex-1 items-center justify-center py-10">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Room not found</CardTitle>
            <CardDescription>
              The code {code} doesn't match any room. Double-check the link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")}>Back to home</Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (loading || !details) {
    return (
      <main className="container py-8">
        <div className="grid gap-4 lg:grid-cols-[280px_1fr_360px]">
          <Skeleton className="h-72" />
          <Skeleton className="h-[420px]" />
          <Skeleton className="h-72" />
        </div>
      </main>
    );
  }

  const meSyncedInRoom = details.members.some(
    (m) => m.steamId === me.steamId && m.lastLibrarySyncAt,
  );

  return (
    <main className="container py-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Room code
          </p>
          <h1 className="font-mono text-3xl font-bold tracking-widest">{details.code}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={copyLink}>
            <Copy />
            Copy invite link
          </Button>
          <Button variant="outline" onClick={handleSync}>
            <RefreshCcw />
            Resync library
          </Button>
          <Button variant="ghost" onClick={handleLeave}>
            <LogOut />
            Leave
          </Button>
        </div>
      </div>

      {!meSyncedInRoom && (
        <Card className="mb-6 border-primary/40 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-base">Sync your library</CardTitle>
            <CardDescription>
              Your library hasn't been imported yet. Sync now so your games count toward
              the wheel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleSync}>Sync now</Button>
          </CardContent>
        </Card>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          type="button"
          variant={roomTab === "wheel" ? "default" : "outline"}
          size="sm"
          onClick={() => setRoomTab("wheel")}
        >
          Library wheel
        </Button>
        <Button
          type="button"
          variant={roomTab === "tournament" ? "default" : "outline"}
          size="sm"
          onClick={() => setRoomTab("tournament")}
        >
          Tournament
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr_360px]">
        <MemberList
          members={details.members}
          ownerSteamId={details.ownerSteamId}
          meSteamId={me.steamId}
        />
        {roomTab === "wheel" ? (
          <GameWheel
            games={details.commonGames}
            disabled={disabled}
            socket={socket}
            roomCode={details.code}
            meSteamId={me.steamId}
            members={details.members}
            mode={mode}
            onModeChange={changeMode}
            onSpinningChange={setIsSpinning}
          />
        ) : (
          <TournamentSection
            code={details.code}
            socket={socket}
            me={me}
            details={details}
            disabled={disabled}
            editsLocked={isSpinning || isTournamentSpinning}
            onTournamentSpinningChange={setIsTournamentSpinning}
          />
        )}
        <GameList
          games={details.commonGames}
          disabled={disabled}
          highlightAppId={highlightAppId}
          onToggle={toggleGame}
          onBulkToggle={bulkToggleGames}
          onAddCustom={addCustomGame}
          onRemoveCustom={removeCustomGame}
          locked={isSpinning || isTournamentSpinning}
        />
      </div>
    </main>
  );
}
